import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Row,
  Col,
  Form,
  Input,
  Switch,
  message,
  Divider,
  Typography,
  Alert,
  Spin
} from 'antd';
import {
  CheckCircleOutlined
} from '@ant-design/icons';
import { gamificationApi } from '../../../shared/api/gamificationApi';
import { GamificationConfig } from './types';

const { Text } = Typography;
const { TextArea } = Input;

export const MessagesTab: React.FC = () => {
  const [form] = Form.useForm();
  const [configForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [previewData, setPreviewData] = useState({
    hasLevelUp: true,
    hasBonus: true,
    hasStreak: true,
    hasNextObjective: true,
  });

  // States locales para controlar los switches
  const [showBonusInMessage, setShowBonusInMessage] = useState(true);
  const [showProgressInMessage, setShowProgressInMessage] = useState(true);
  const [showNextObjective, setShowNextObjective] = useState(true);

  const defaultTemplate = `🎉 **¡SUBISTE DE NIVEL!** 🎉
Has alcanzado el nivel **{newLevel}: {newLevelName}**
¡Felicitaciones!

─────────────────

🎁 **Bonus aplicados:**
{bonusDetails}

**Total: +\${totalBonus} +{totalPoints} pts**

─────────────────

📊 **Tu progreso:**
🏆 Nivel: {levelText}
{streakEmoji} Racha: {currentStreak} días
📈 Total cargas: {totalLoads}
💰 Bonus acumulado: \${lifetimeBonus}
🎯 Puntos: {currentPoints} pts

─────────────────

🎯 **Próximo objetivo:**
{objectiveName}
Progreso: {objectiveProgress}%`;

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      setInitialLoading(true);
      const config: GamificationConfig = await gamificationApi.getConfig();

      // Si no hay template configurado, mostrar mensaje informativo del sistema dinámico
      const dynamicTemplateInfo = `🔄 Template Dinámico Activado

El sistema ahora usará mensajes inteligentes que se adaptan automáticamente:
• Si sube de nivel → Mensaje celebratorio
• Si NO sube → Muestra nivel actual y progreso
• Si hay bonus → Muestra detalles
• Si NO hay bonus → Omite la sección
• Siempre muestra estado actual y próximos objetivos

Para personalizar, puedes editar el template manualmente.`;

      const template = config.messageTemplate || dynamicTemplateInfo;
      setMessageTemplate(template);
      form.setFieldsValue({ messageTemplate: template });

      // Cargar configuración de switches
      const bonusValue = config.showBonusInMessage ?? true;
      const progressValue = config.showProgressInMessage ?? true;
      const objectiveValue = config.showNextObjective ?? true;

      setShowBonusInMessage(bonusValue);
      setShowProgressInMessage(progressValue);
      setShowNextObjective(objectiveValue);

      configForm.setFieldsValue({
        showBonusInMessage: bonusValue,
        showProgressInMessage: progressValue,
        showNextObjective: objectiveValue,
      });
    } catch (error) {
      message.error('Error al cargar la plantilla de mensaje');
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 🔧 FIX: Convertir literales \n en saltos de línea reales antes de guardar
      let cleanedTemplate = values.messageTemplate;
      cleanedTemplate = cleanedTemplate.replace(/\\n/g, '\n');

      await gamificationApi.updateConfig({
        messageTemplate: cleanedTemplate
      });
      message.success('Plantilla de mensaje actualizada correctamente');
      setMessageTemplate(cleanedTemplate);

      // Actualizar el formulario con la versión limpia
      form.setFieldsValue({ messageTemplate: cleanedTemplate });
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Por favor completa todos los campos requeridos');
      } else {
        message.error('Error al actualizar la plantilla');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);

      // Mensaje para el usuario sobre el template dinámico
      const dynamicTemplateInfo = `🔄 Template Dinámico Activado

El sistema ahora usará mensajes inteligentes que se adaptan automáticamente:
• Si sube de nivel → Mensaje celebratorio
• Si NO sube → Muestra nivel actual y progreso
• Si hay bonus → Muestra detalles
• Si NO hay bonus → Omite la sección
• Siempre muestra estado actual y próximos objetivos

Para personalizar, puedes editar el template manualmente.`;

      // Resetear en el formulario con mensaje informativo
      form.setFieldsValue({ messageTemplate: dynamicTemplateInfo });
      setMessageTemplate(dynamicTemplateInfo);

      // Guardar NULL en el backend para activar el template dinámico
      await gamificationApi.updateConfig({
        messageTemplate: null
      });

      message.success('✅ Template dinámico activado - Los mensajes se adaptarán automáticamente al contexto');
    } catch (error) {
      message.error('Error al resetear plantilla');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = async (field: string, value: boolean) => {
    try {
      // Actualizar state local inmediatamente
      if (field === 'showBonusInMessage') setShowBonusInMessage(value);
      if (field === 'showProgressInMessage') setShowProgressInMessage(value);
      if (field === 'showNextObjective') setShowNextObjective(value);

      await gamificationApi.updateConfig({ [field]: value });
      message.success('Configuración actualizada');
    } catch (error) {
      message.error('Error al actualizar la configuración');
      console.error(error);
      // Revertir en caso de error
      if (field === 'showBonusInMessage') setShowBonusInMessage(!value);
      if (field === 'showProgressInMessage') setShowProgressInMessage(!value);
      if (field === 'showNextObjective') setShowNextObjective(!value);
    }
  };

  const renderPreview = () => {
    let template = form.getFieldValue('messageTemplate') || messageTemplate;

    // Si es el mensaje informativo del template dinámico, mostrar un preview de ejemplo
    if (template.includes('🔄 Template Dinámico Activado')) {
      return `💰 ✨ ¡Saldo Cargado Exitosamente! ✨

🏆 Nivel Actual: Bronce
📈 Progreso: 67% para alcanzar Plata

━━━━━━━━━━━━━━━
📊 Tu Progreso Actual:
🔥🔥 Racha: 3 días consecutivos
📈 Total de cargas: 8
🎯 Puntos acumulados: 25 pts
💰 Bonus total ganado: 75.00

━━━━━━━━━━━━━━━
🎯 Próximo Objetivo:
Completa 2 cargas más para alcanzar Plata
Progreso: 80%

¡Sigue así! 🚀

📝 Nota: Este es un ejemplo. El mensaje real se adaptará automáticamente según el contexto (si subió de nivel, si hay bonus, etc.)`;
    }

    // 🔧 FIX: Convertir literales \n en saltos de línea reales
    // Si detectamos \\n (escapado) o \n literal como texto, convertirlos
    template = template.replace(/\\n/g, '\n');

    // Datos de ejemplo para el preview
    const sampleData: Record<string, string> = {
      // Nivel
      hasLevelUp: previewData.hasLevelUp ? '1' : '0',
      oldLevel: '1',
      newLevel: '2',
      newLevelName: 'Jugador Silver',
      currentLevel: '2',
      levelName: 'Jugador Silver',
      levelText: 'Jugador Silver (45% al siguiente)',
      levelProgress: '45',

      // Bonus
      hasBonus: previewData.hasBonus ? '1' : '0',
      totalBonus: '150.00',
      bonusDetails: '⭐ Primera Carga del Día: +$100.00 +5 pts\n• Bonus de Racha (3 días): +$50.00',
      bonusCount: '2',

      // Puntos
      totalPoints: '5',
      currentPoints: '25',
      hasPoints: '1',

      // Racha
      currentStreak: '3',
      longestStreak: '7',
      streakEmoji: '🔥🔥',
      hasStreak: previewData.hasStreak ? '1' : '0',

      // Estadísticas
      totalLoads: '15',
      lifetimeBonus: '450.00',

      // Próximo objetivo
      hasNextObjective: previewData.hasNextObjective ? '1' : '0',
      objectiveName: 'Alcanza 20 cargas totales',
      objectiveDescription: 'Completa 20 cargas para subir a nivel 3',
      objectiveProgress: '75',
    };

    let preview = template;
    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      preview = preview.replace(regex, value);
    });

    return preview;
  };

  if (initialLoading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  // Detectar problemas en la plantilla
  const currentTemplate = form.getFieldValue('messageTemplate') || messageTemplate;
  const hasLiteralNewlines = currentTemplate.includes('\\n');
  const hasDuplicatedContent = currentTemplate.split('Saldo acreditado').length > 2;
  const hasTemplateIssues = hasLiteralNewlines || hasDuplicatedContent;

  return (
    <div>
      <Alert
        message="Sistema de Mensajes Inteligente"
        description="Diseña un mensaje único que se adapta automáticamente según el contexto. Si no hay bonus, no se mostrará la sección de bonus. Si no hay racha activa, se omitirá esa información. ¡El mensaje se construye dinámicamente!"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* ADVERTENCIA si hay problemas en la plantilla */}
      {hasTemplateIssues && (
        <Alert
          message="⚠️ Problemas detectados en la plantilla"
          description={
            <div>
              {hasLiteralNewlines && <div>• Se detectaron literales <code>\n</code> en el texto. Los saltos de línea deben ser reales (presiona Enter en lugar de escribir \n).</div>}
              {hasDuplicatedContent && <div>• Se detectó contenido duplicado. Revisa que no haya texto repetido innecesariamente.</div>}
              <div style={{ marginTop: 8 }}>
                <strong>Solución:</strong> Haz clic en "Restaurar Predeterminado" para usar la plantilla limpia.
              </div>
            </div>
          }
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Configuración de Mensajes */}
      <Card
        title="💬 Configuración de Mensajes"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Switch
              checked={showBonusInMessage}
              onChange={(checked) => {
                handleConfigChange('showBonusInMessage', checked);
              }}
            />
            <Text>Mostrar detalles de bonus aplicados en notificaciones</Text>
          </Space>

          <Space>
            <Switch
              checked={showProgressInMessage}
              onChange={(checked) => {
                handleConfigChange('showProgressInMessage', checked);
              }}
            />
            <Text>Mostrar progreso del usuario (nivel, racha, estadísticas)</Text>
          </Space>

          <Space>
            <Switch
              checked={showNextObjective}
              onChange={(checked) => {
                handleConfigChange('showNextObjective', checked);
              }}
            />
            <Text>Mostrar próximo objetivo alcanzable</Text>
          </Space>
        </Space>
      </Card>

      <Divider />

      {/* Editor y Vista Previa en columnas */}
      <Row gutter={16}>
        {/* Columna izquierda: Editor de plantilla */}
        <Col xs={24} lg={12}>
          <Card
            title="✏️ Editor de Plantilla"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Form form={form} layout="vertical">
              <Form.Item
                name="messageTemplate"
                label="Plantilla del Mensaje"
                rules={[{ required: true, message: 'La plantilla es requerida' }]}
                extra="Personaliza el mensaje usando las variables disponibles abajo."
              >
                <TextArea
                  rows={20}
                  placeholder={defaultTemplate}
                  onChange={(e) => setMessageTemplate(e.target.value)}
                />
              </Form.Item>

              <Space>
                <Button
                  type="primary"
                  onClick={handleSave}
                  loading={loading}
                  icon={<CheckCircleOutlined />}
                  size="large"
                >
                  Guardar Plantilla
                </Button>
                <Button
                  onClick={handleReset}
                  size="large"
                  danger={hasTemplateIssues}
                  type={hasTemplateIssues ? 'primary' : 'default'}
                  loading={loading}
                >
                  {hasTemplateIssues ? '🔧 Restaurar Predeterminado (Recomendado)' : 'Restaurar Predeterminado'}
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        {/* Columna derecha: Vista Previa */}
        <Col xs={24} lg={12}>
          <Card
            title="👁️ Vista Previa"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Space size="small">
                <Text type="secondary" style={{ fontSize: 12 }}>Escenario:</Text>
                <Switch
                  checkedChildren="Con Nivel"
                  unCheckedChildren="Sin Nivel"
                  checked={previewData.hasLevelUp}
                  onChange={(checked) => setPreviewData({ ...previewData, hasLevelUp: checked })}
                  size="small"
                />
                <Switch
                  checkedChildren="Con Bonus"
                  unCheckedChildren="Sin Bonus"
                  checked={previewData.hasBonus}
                  onChange={(checked) => setPreviewData({ ...previewData, hasBonus: checked })}
                  size="small"
                />
              </Space>
            }
          >
            <div style={{
              padding: 16,
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.6,
              minHeight: 500,
              maxHeight: 600,
              overflow: 'auto',
              border: '1px solid var(--ant-color-border)',
              backgroundColor: 'var(--ant-color-bg-container)',
            }}>
              {renderPreview()}
            </div>
            <Alert
              message="Esto es cómo se verá el mensaje para el usuario"
              type="success"
              showIcon
              style={{ marginTop: 12 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Variables Disponibles - ABAJO de todo */}
      <Card
        title="📋 Variables Disponibles"
        size="small"
        style={{ marginTop: 16 }}
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <div>
                <Text strong type="warning">🏆 Nivel:</Text>
                <div style={{ paddingLeft: 16, marginTop: 8 }}>
                  <Text code>{'{hasLevelUp}'}</Text> <Text type="secondary">- Si subió de nivel (1/0)</Text><br/>
                  <Text code>{'{newLevel}'}</Text> <Text type="secondary">- Número del nuevo nivel</Text><br/>
                  <Text code>{'{newLevelName}'}</Text> <Text type="secondary">- Nombre del nuevo nivel</Text><br/>
                  <Text code>{'{levelText}'}</Text> <Text type="secondary">- Texto completo del nivel actual</Text><br/>
                  <Text code>{'{levelProgress}'}</Text> <Text type="secondary">- Progreso al siguiente nivel (%)</Text>
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text strong type="warning">🎁 Bonus:</Text>
                <div style={{ paddingLeft: 16, marginTop: 8 }}>
                  <Text code>{'{hasBonus}'}</Text> <Text type="secondary">- Si recibió bonus (1/0)</Text><br/>
                  <Text code>{'{totalBonus}'}</Text> <Text type="secondary">- Monto total del bonus</Text><br/>
                  <Text code>{'{bonusDetails}'}</Text> <Text type="secondary">- Detalle de cada regla aplicada</Text><br/>
                  <Text code>{'{bonusCount}'}</Text> <Text type="secondary">- Cantidad de reglas aplicadas</Text>
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text strong type="warning">🎯 Puntos:</Text>
                <div style={{ paddingLeft: 16, marginTop: 8 }}>
                  <Text code>{'{hasPoints}'}</Text> <Text type="secondary">- Si recibió puntos (1/0)</Text><br/>
                  <Text code>{'{totalPoints}'}</Text> <Text type="secondary">- Puntos ganados en esta carga</Text><br/>
                  <Text code>{'{currentPoints}'}</Text> <Text type="secondary">- Puntos acumulados totales</Text>
                </div>
              </div>
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <div>
                <Text strong type="warning">🔥 Racha:</Text>
                <div style={{ paddingLeft: 16, marginTop: 8 }}>
                  <Text code>{'{hasStreak}'}</Text> <Text type="secondary">- Si tiene racha activa (1/0)</Text><br/>
                  <Text code>{'{currentStreak}'}</Text> <Text type="secondary">- Días de racha actual</Text><br/>
                  <Text code>{'{streakEmoji}'}</Text> <Text type="secondary">- Emoji según la racha (🔥/🔥🔥/🔥🔥🔥)</Text>
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text strong type="warning">📊 Estadísticas:</Text>
                <div style={{ paddingLeft: 16, marginTop: 8 }}>
                  <Text code>{'{totalLoads}'}</Text> <Text type="secondary">- Total de cargas realizadas</Text><br/>
                  <Text code>{'{lifetimeBonus}'}</Text> <Text type="secondary">- Bonus acumulado histórico</Text>
                </div>
              </div>
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <div>
                <Text strong type="warning">🎯 Próximo Objetivo:</Text>
                <div style={{ paddingLeft: 16, marginTop: 8 }}>
                  <Text code>{'{hasNextObjective}'}</Text> <Text type="secondary">- Si hay objetivo (1/0)</Text><br/>
                  <Text code>{'{objectiveName}'}</Text> <Text type="secondary">- Nombre del objetivo</Text><br/>
                  <Text code>{'{objectiveProgress}'}</Text> <Text type="secondary">- Progreso del objetivo (%)</Text>
                </div>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>
    </div>
  );
};
